<i18n>
{
  "fr": {
    "publicId": "ID cantonal",
    "wellDbk": "ID REGIS",
    "station-title": "Point d'eau: ",
    "show-detail": "Voir plus de détail",
    "hide-detail": "Voir moins de détail",
    "sourceNm": "Source",
    "type": "Type",
    "xCrd": "X",
    "yCrd": "Y",
    "originalEnddepth": "Profondeur initiale",
    "currentEnddepth": "Profondeur courante",
    "owner": "Propriétaire",
    "status": "Statut",
    "remark": "Remarque",
    "geocadast": "ID Geocadast"
  },
  "de": {

  }
}
</i18n>

<template>
  <div id="obsPoint-info" class="tool" v-if="show">
    <div class="header">
      <div class="header-content">
        <h3>{{ $t('station-title') }} {{ currentObject.name }}</h3>
        <div class="action">
          <md-button :class="['md-icon-button', 'md-raised', 'md-dense', {'md-primary': showDetail}]" @click="toggleDetail">
            <md-icon>read_more</md-icon>
            <md-tooltip>{{ $t(showDetail ? 'hide-detail' : 'show-detail') }}</md-tooltip>
          </md-button>
        </div>
      </div>
    </div>


    <div class="main-container">
      <div class="info" v-if="showDetail">
        <h4>{{ $t('wellDbk') }}</h4>
        <span class="info-value">{{ currentObject.wellDbk }}</span>
      </div>

      <div class="info">
        <h4>{{ $t('publicId') }}</h4>
        <span class="info-value">{{ currentObject.publicId }}</span>
      </div>

      <template v-if="showDetail">

        <div class="info" v-if="currentObject.geocadastId !== ''">
          <h4>{{ $t('geocadast') }}</h4>
          <span class="info-value"><a :href="`https://geocadast.crealp.ch/index.php?lang=FR&view=sondage&mode=rapport&id=${currentObject.geocadastId}`" target="_blank">{{ currentObject.geocadastId }}</a></span>
        </div>

        <div class="info">
          <h4>{{ $t('sourceNm') }}</h4>
          <span class="info-value">{{ currentObject.sourceNm }}</span>
        </div>

        <div class="info">
          <h4>{{ $t('type') }}</h4>
          <span class="info-value">{{ currentObject.typeFr }}</span>
        </div>

        <div class="info">
          <h4>{{ $t('xCrd') }}</h4>
          <span class="info-value">{{ currentGeom.coordinates[0] | formatCoordinate }}</span>
        </div>

        <div class="info y-crd">
          <h4>{{ $t('yCrd') }}</h4>
          <span class="info-value">{{ currentGeom.coordinates[1] | formatCoordinate }}</span>
        </div>

        <div class="info">
          <h4>{{ $t('originalEnddepth') }}</h4>
          <span class="info-value">{{ currentObject.originalEnddepth | formatDepth }}</span>
        </div>

        <div class="info">
          <h4>{{ $t('currentEnddepth') }}</h4>
          <span class="info-value">{{ currentObject.currentEnddepth | formatDepth }}</span>
        </div>

        <div class="info">
          <h4>{{ $t('owner') }}</h4>
          <span class="info-value">{{ currentObject.ownerNm | formatNullValue }}</span>
        </div>

        <div class="info">
          <h4>{{ $t('status') }}</h4>
          <span class="info-value">{{ currentObject.statusFr }}</span>
        </div>

        <div class="info">
          <h4>{{ $t('remark') }}</h4>
          <span class="info-value">{{ currentObject.remark | formatNullValue }}</span>
        </div>

      </template>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import MapStoreMixin from '@/mixins/store/map'
import LayerStoreMixin from '@/mixins/store/layer'
import ToolStoreMixin from '@/mixins/store/tool'
import { Style, Icon } from 'ol/style'
import DefaultObsPointHover from '@/assets/icons/hover-icons/default-obs-point-hover.svg'

export default {
  name: 'ObsPointDescription',
  mixins: [
    MapStoreMixin,
    LayerStoreMixin,
    ToolStoreMixin
  ],
  props: {
    params: {
      type: Object,
      default: () => { return {} }
    }
  },
  data () {
    return  {
      showDetail: false
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.updateTool()
    })
  },
  beforeDestroy() {
    this.removeTool()
    this.clearHoverFeature()
  },
  watch: {
    currentGeom () {
      this.updateTool()
    },
    selectedLayers () {
      this.updateTool()
    }
  },
  computed: {
    nodata () {
      return !this.loading && _.isEmpty(this.currentObject)
    },
    show () {
      return this.currentGeom !== null && this.selectedLayers.length > 0
    },
    hasRemoteData () {
      return this.datasources?.filter((ds) => ds.datasourceType.code === 'remote').length > 0
    },
    datasources () {
      if (_.isEmpty(this.currentObject)) return []
      return this.currentObject.datasources
    },
    workingState () {
      let datasources = this.datasources?.filter((ds) => _.includes(this.params.parameterTypeCodes, ds.parameterType.code))
      let datasourcesNodata = this.hasRemoteData ? datasources.filter((ds) => ds.workingState === 'nodata' && ds.datasourceType.code === 'remote') : this.datasources.filter((ds) => ds.workingState === 'nodata' && ds.datasourceType === 'validated')
      let datasourcesWarn = datasources.filter((ds) => ds.workingState === 'warn')
      let datasourcesErr = datasources.filter((ds) => ds.workingState === 'err')

      if (datasourcesNodata.length > 0) return 'nodata'
      if (datasourcesErr.length > 0) return 'err'
      if (datasourcesWarn.length > 0) return 'warn'
      return 'ok'
    }
  },
  filters: {
    formatNullValue (value) {
      if (value === '' || value === 'N/A') return '-'
      return value
    },
    formatDepth (value) {
      if (value === '' || value === '999.99') return '-'
      return `${parseFloat(value).toFixed(2)} m`
    },
    formatCoordinate (value) {
      if (value === undefined) return ''

      return new Intl.NumberFormat('fr-CH', {
        style: 'decimal'
      }).format(value.toFixed(0)).replaceAll(' ', '\'')
    }
  },
  methods: {
    updateTool () {
      if (this.selectedLayers.length > 0 && this.currentGeom !== null && this.currentObject !== null) {
        this.load()
      } else {
        this.removeTool()
      }
    },
    removeTool () {
      if (this.hoverLayer === null) return
      this.mapRef.removeLayer(this.hoverLayer)
      this.hoverLayer = null
    },
    load () {
      this.showDetail = this.params.showDetail

      this.setHoverGeoJSON(this.currentGeom)
      this.setHoverStyle(new Style({
        image: new Icon({
          opacity: 1,
          src: DefaultObsPointHover,
          scale: 0.35
        })
      }))
    },
    toggleDetail () {
      this.showDetail = !this.showDetail
    },
    gotoObsPointAdmin () {
      let url = this.$router.resolve({ name: 'admin-observation-point-data', params: { id: this.currentObject.id } })
      window.open(url.href, '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
  #obsPoint-info {
    width: 100%;

    .header {
      display: flex;
      flex-direction: column;

      .header-content {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }

    .main-container {
      overflow-y: auto;
      overflow-x: hidden;
      background: none;

      padding-left: 23px;
      padding-right: 23px;

      @media (max-height: 500px) {
        max-height: 100vh;
      }
    }

    .container {
      margin-left: -10px;
      margin-right: -10px;
      background-color: white;

      h2 {
        margin-top: 5px;
      }
    }

    h3 {
      max-width: 17vw;
    }

    h4 {
      margin: 0px;
      font-size: 13px;
    }

    .info {
      display: flex;
      align-items: center;
      margin-top: 10px;

      h4 {
        min-width: 140px;
      }

      .info-value {
        margin-left: 5px;
      }

      &.y-crd {
        margin-top: -5px;
      }
    }
  }
</style>
